import React, { useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { History, X } from "lucide-react";
import { api, API_BASE } from "../lib/api";
import OracleResponseActions from "../components/OracleResponseActions";
import OracleHistory from "../components/OracleHistory";
import LoginPrompt from "../components/LoginPrompt";
import { useAuth } from "../contexts/AuthContext";
import { weeklyOracleStarters } from "../lib/oraclePrompts";

function OraclePrelude() {
  const [text, setText] = useState("");
  const [saved, setSaved] = useState(null);
  const [skipped, setSkipped] = useState(false);
  useEffect(() => {
    api.get("/users/me/preferences")
      .then(({ data }) => { if (data.oracle_context) setSaved(data.oracle_context); })
      .catch(() => {});
  }, []);
  if (saved || skipped) return null;
  const save = async (e) => {
    e.preventDefault();
    if (!text.trim()) { setSkipped(true); return; }
    try { await api.patch("/users/me/preferences", { oracle_context: text }); setSaved(text); }
    catch (err) { console.error(err); setSkipped(true); }
  };
  return (
    <form onSubmit={save} data-testid="oracle-prelude" className="p-6 hairline mb-6">
      <div className="label mb-3">Before we begin (optional)</div>
      <p className="serif italic text-[hsl(var(--oracle-foreground)/0.8)] mb-4">Where are you coming from? Anything you write helps me meet you there. Or skip — I'll meet you anyway.</p>
      <textarea value={text} onChange={(e) => setText(e.target.value)} rows={2} maxLength={500}
                placeholder="e.g. raised Christian, reading Jung, curious about Buddhism…"
                className="input-line w-full text-[14px]" data-testid="oracle-prelude-input" />
      <div className="mt-4 flex gap-3">
        <button type="submit" className="btn-oracle" data-testid="oracle-prelude-save">Continue</button>
        <button type="button" onClick={() => setSkipped(true)} data-testid="oracle-prelude-skip" className="btn-ghost">Skip</button>
      </div>
    </form>
  );
}


function newSessionId() {
  return `oracle_${Math.random().toString(36).slice(2, 10)}_${Date.now()}`;
}

export default function Oracle() {
  const { user, loading: authLoading } = useAuth();
  const [sessionId, setSessionId] = useState(newSessionId);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [streaming, setStreaming] = useState(false);
  const [historyOpenMobile, setHistoryOpenMobile] = useState(false);
  const [historyVersion, setHistoryVersion] = useState(0);
  // Map<`${question}|||${response}`, {id, visibility}> — indexes every save
  // this user has, so we can flag prior "Previously shared" turns when they
  // reopen a historical session. Refreshed on mount and whenever a new save
  // happens (via historyVersion bump).
  const [shareRegistry, setShareRegistry] = useState(() => new Map());
  const scrollRef = useRef(null);
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    if (!user) return;
    let cancelled = false;
    api.get("/oracle/saves/me")
      .then(({ data }) => {
        if (cancelled) return;
        const map = new Map();
        for (const s of data || []) {
          if (s.question && s.response) {
            map.set(`${s.question}|||${s.response}`, { id: s.id, visibility: s.visibility });
          }
        }
        setShareRegistry(map);
      })
      .catch(() => { /* non-fatal — the pill just won't appear */ });
    return () => { cancelled = true; };
  }, [user, historyVersion]);

  const autoAskedRef = useRef(false);

  // Accept ?q=... to prefill the input — used by TraditionDetail "Ask the Oracle".
  // With ?ask=1 (or alias `auto=1`), the question is sent immediately so the
  // user lands directly on the streaming response instead of an extra "Ask" tap.
  // Reads once on mount, then strips the params so a manual reload doesn't re-fire.
  useEffect(() => {
    if (autoAskedRef.current) return;
    const params = new URLSearchParams(location.search);
    const q = params.get("q");
    if (!q) return;
    autoAskedRef.current = true;
    const autoAsk = params.get("ask") === "1" || params.get("auto") === "1";
    if (autoAsk) {
      // Fire-and-forget — `send` reads its arg, not state, so we don't need
      // to wait for setInput to commit.
      send(null, q);
    } else {
      setInput(q);
    }
    navigate(location.pathname, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [messages]);

  const startNewChat = () => {
    setSessionId(newSessionId());
    setMessages([]);
    setInput("");
    setHistoryOpenMobile(false);
  };

  const loadSession = (sid, msgs) => {
    setSessionId(sid);
    // Defensively coerce — an unexpected null role/content used to throw on
    // `m.role[0]` and blanked the whole panel silently.
    const safe = (Array.isArray(msgs) ? msgs : [])
      .filter((m) => m && (m.role === "user" || m.role === "assistant"))
      .map((m, i) => ({
        id: `${m.role[0]}-${i}-${m.created_at || Date.now()}`,
        role: m.role,
        content: typeof m.content === "string" ? m.content : "",
      }));
    setMessages(safe);
    setHistoryOpenMobile(false);
    // Scroll to bottom after render
    setTimeout(() => { if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight; }, 100);
  };

  const send = async (e, override) => {
    e?.preventDefault();
    // Allow callers (e.g. the ?q=&ask=1 deep-link) to supply the prompt directly
    // so we don't have to wait for setInput to commit before submitting.
    const msg = (typeof override === "string" ? override : input).trim();
    if (!msg || streaming) return;
    setInput("");
    const userId = `u-${Date.now()}`;
    const asstId = `a-${Date.now() + 1}`;
    setMessages((m) => [...m, { id: userId, role: "user", content: msg }, { id: asstId, role: "assistant", content: "" }]);
    setStreaming(true);

    try {
      const res = await fetch(`${API_BASE}/oracle/stream`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          // Must match the axios interceptor — see lib/api.js.
          "X-Correlate-Client": "web",
        },
        credentials: "include",
        body: JSON.stringify({ session_id: sessionId, message: msg }),
      });
      if (!res.ok || !res.body) throw new Error("stream failed");
      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let buffer = "";
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        const parts = buffer.split("\n\n");
        buffer = parts.pop() || "";
        for (const part of parts) {
          const line = part.split("\n").find((l) => l.startsWith("data: "));
          if (!line) continue;
          const payload = line.slice(6).trim();
          if (payload === "[DONE]") continue;
          try {
            const { delta } = JSON.parse(payload);
            if (delta) {
              setMessages((m) => {
                if (!m.length) return m;
                const last = m[m.length - 1];
                if (!last) return m;
                const copy = m.slice();
                copy[copy.length - 1] = { ...last, content: (last.content || "") + delta };
                return copy;
              });
            }
          } catch (parseErr) {
            console.warn("oracle SSE parse error", parseErr, payload);
          }
        }
      }
    } catch (err) {
      console.error("oracle stream error", err);
      setMessages((m) => {
        if (!m.length) return m;
        const last = m[m.length - 1];
        if (!last) return m;
        const copy = m.slice();
        copy[copy.length - 1] = { ...last, content: "[The Oracle is silent — connection lost.]" };
        return copy;
      });
    } finally {
      setStreaming(false);
      // Bump the refresh counter so the history sidebar picks up the new/updated session.
      setHistoryVersion((v) => v + 1);
    }
  };

  const suggestions = weeklyOracleStarters(4);

  // Auth gate — the Oracle costs LLM tokens per turn and is the app's
  // primary hook, so anonymous visitors are asked to sign in before they
  // can converse. This also gives us a session id to hang saves,
  // reactions, and shares on. Skip while auth is still resolving so we
  // don't flash the gate at signed-in users on a slow /auth/me.
  if (!authLoading && !user) {
    return (
      <div data-testid="oracle-page-gated" className="oracle-theme min-h-[calc(100vh-80px)]">
        <LoginPrompt
          title="The Oracle answers those it can address."
          blurb="Create an account or sign in so your questions, saves, and threads are held for you across sessions. It takes a breath."
        />
      </div>
    );
  }

  return (
    <div data-testid="oracle-page" className="oracle-theme min-h-[calc(100vh-80px)]">
      <div className="max-w-7xl mx-auto px-6 sm:px-10 py-20 grid grid-cols-1 lg:grid-cols-[280px_1fr] gap-10">
        {/* HISTORY SIDEBAR — desktop */}
        <div className="hidden lg:block">
          <div className="sticky top-24">
            <OracleHistory
              currentSessionId={sessionId}
              onLoadSession={loadSession}
              onNewChat={startNewChat}
              refreshKey={historyVersion}
            />
          </div>
        </div>

        {/* MOBILE history drawer */}
        {historyOpenMobile && (
          <div className="fixed inset-0 z-50 lg:hidden" data-testid="oracle-history-drawer">
            <div className="absolute inset-0 bg-black/55 backdrop-blur-sm" onClick={() => setHistoryOpenMobile(false)} />
            <div className="absolute top-0 left-0 bottom-0 w-[88%] max-w-sm bg-[hsl(var(--oracle-background))] text-[hsl(var(--oracle-foreground))] shadow-2xl p-5 overflow-y-auto">
              <div className="flex justify-end mb-3">
                <button onClick={() => setHistoryOpenMobile(false)} className="text-[hsl(var(--oracle-foreground)/0.7)] hover:text-[hsl(var(--oracle-foreground))]" data-testid="oracle-history-close">
                  <X size={18} />
                </button>
              </div>
              <OracleHistory
                currentSessionId={sessionId}
                onLoadSession={loadSession}
                onNewChat={startNewChat}
                refreshKey={historyVersion}
              />
            </div>
          </div>
        )}

        {/* MAIN COLUMN */}
        <div className="flex flex-col min-h-[calc(100vh-80px)]">
          <header className="mb-12">
            <div className="flex items-center justify-between gap-4 mb-6">
              <div className="label">The Oracle</div>
              <button
                data-testid="oracle-history-toggle"
                onClick={() => setHistoryOpenMobile(true)}
                className="reaction-pill lg:hidden"
                title="Open conversations"
              >
                <History size={12} strokeWidth={2} /> History
              </button>
            </div>
            <h1 className="serif text-5xl sm:text-6xl leading-tight">
              Ask — and the parallels surface.
            </h1>
            <p className="mt-6 text-[hsl(var(--oracle-foreground)/0.75)] max-w-2xl leading-relaxed">
              A scholar-mystic trained on the archive. It cites traditions inline, labels every source as
              Academic, Esoteric, Speculative, or Redacted — and ends with a line written for the seeker,
              for you.
            </p>
            <p className="mt-4 serif italic text-[hsl(var(--oracle-accent))]">
              Bring the question that has been following you around. Nothing here is too strange.
            </p>
          </header>

          <div ref={scrollRef} className="flex-1 overflow-y-auto space-y-10 pb-8" data-testid="oracle-thread">
            {messages.length === 0 && (
              <div className="space-y-3">
                {/* Composer sits directly under the header intro so a first-time
                    seeker can start typing without hunting for the input. */}
                <form onSubmit={send} className="mb-10" data-testid="oracle-form-empty">
                  <div className="oracle-composer">
                    <div className="oracle-abyss">
                      <textarea
                        data-testid="oracle-input"
                        aria-label="Ask the Oracle"
                        value={input}
                        onChange={(e) => setInput(e.target.value)}
                        onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) send(e); }}
                        rows={1}
                        placeholder=""
                        disabled={streaming}
                      />
                    </div>
                    <button
                      type="submit"
                      data-testid="oracle-send"
                      disabled={streaming || !input.trim()}
                      className="oracle-send-btn"
                    >
                      {streaming ? "Listening…" : "Ask"}
                    </button>
                  </div>
                </form>
                <OraclePrelude />
                <div className="label mb-4 mt-10">Try asking</div>
                {suggestions.map((s) => (
                  <button
                    key={s}
                    data-testid={`oracle-suggestion`}
                    onClick={() => { setInput(s); }}
                    className="block w-full text-left p-5 hairline bg-transparent hover:border-[hsl(var(--oracle-accent))] transition-colors group"
                  >
                    <span className="serif italic text-lg text-[hsl(var(--oracle-foreground)/0.85)] group-hover:text-[hsl(var(--oracle-accent))]">{s}</span>
                  </button>
                ))}
              </div>
            )}

            {messages.map((m, i) => {
              // Show the action bar under every COMPLETED assistant response
              // — historical + live. The only gate is streaming: while an
              // answer is still being written, the actions would be pointing
              // at half-finished text, so we suppress them until it settles.
              const isLast = i === messages.length - 1;
              const isCompleteAssistant =
                m.role === "assistant"
                && (m.content || "").length > 40
                && (!isLast || !streaming);
              // The previous user message is the question that produced this answer.
              const question = i > 0 && messages[i - 1].role === "user" ? messages[i - 1].content : "";
              return (
                <div key={m.id} data-testid={`oracle-msg-${m.role}-${i}`}>
                  <div className="label mb-3">{m.role === "user" ? "You" : "The Oracle"}</div>
                  <div className={`${m.role === "user" ? "serif text-2xl italic text-[hsl(var(--oracle-foreground))]" : "text-[hsl(var(--oracle-foreground)/0.9)] leading-[1.85] whitespace-pre-wrap"} max-w-3xl`}>
                    {m.content || <span className="text-[hsl(var(--oracle-foreground)/0.45)] italic">…listening to the field…</span>}
                  </div>
                  {isCompleteAssistant && (
                    <OracleResponseActions
                      question={question}
                      response={m.content}
                      sessionId={sessionId}
                      msgId={m.id}
                      preShared={shareRegistry.get(`${question}|||${m.content}`) || null}
                    />
                  )}
                </div>
              );
            })}
          </div>

          {/* Bottom composer — only shown once a conversation has started. In the
              empty state the composer lives inside the thread block, right under
              the header intro. */}
          {messages.length > 0 && (
            <form onSubmit={send} className="pt-8" data-testid="oracle-form">
              <div className="oracle-composer">
                <div className="oracle-abyss">
                  <textarea
                    data-testid="oracle-input-bottom"
                    aria-label="Ask the Oracle"
                    value={input}
                    onChange={(e) => setInput(e.target.value)}
                    onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) send(e); }}
                    rows={1}
                    placeholder=""
                    disabled={streaming}
                  />
                </div>
                <button
                  type="submit"
                  data-testid="oracle-send-bottom"
                  disabled={streaming || !input.trim()}
                  className="oracle-send-btn"
                >
                  {streaming ? "Listening…" : "Ask"}
                </button>
              </div>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}
